<script setup lang="ts">
import { FullTableLayout } from '@/components/Layouts'
import PermissionWrapper from '@/components/Permission/PermissionWrapper.vue'
import { BaseTable, TableColumn, TablePagination } from '@/components/Table'
import TableActionColumn from '@/components/Table/TableActionColumn.vue'
import { moment } from '@/locales/langUtils'
import store from '@/store'
import { EShuttleEpcActions } from '@/store/shuttleEpcStore/ShuttleEpcStoreTypes'
import { EStoreModules } from '@/store/storeType'
import { ESortBy, getSortByKey } from '@/types/enum/SortByEnum'
import type { ShuttleEpcModel } from '@/types/models/ShuttleEpcModel'
import { EAdminPerm } from '@/types/permissions/AdminPermissionsEnum'
import { DATE_FORMAT } from '@/utils/dateUtils'
import { getQuerySort } from '@/utils/queryUtils'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import StackLayout from '../../../components/Layouts/StackLayout.vue'
import AdminShuttleCreateBulkEpc from './components/AdminShuttleCreateBulkEpc.vue'
import AdminShuttleCreateEpc from './components/AdminShuttleCreateEpc.vue'

const { t } = useI18n()

const data = computed(() => store.state.shuttleEpc)
const sort = ref<ESortBy>(getQuerySort() ?? ESortBy.DATE_RECENT)

const getShuttleEpcs = () =>
    store.dispatch(`${EStoreModules.SHUTTLE_EPC}/${EShuttleEpcActions.FETCH_SHUTTLE_EPCS}`, {
        sort: getSortByKey(sort.value),
        per_page: data.value.per_page,
        page: data.value.current_page
    })

const deleteShuttleEpc = (shuttleEpc: ShuttleEpcModel) => {
    store
        .dispatch(`${EStoreModules.SHUTTLE_EPC}/${EShuttleEpcActions.DELETE_SHUTTLE_EPC}`, {
            id: shuttleEpc.id,
            epc: shuttleEpc.epc
        })
        .then(() => getShuttleEpcs())
}

const handleChangePageSize = (value: number) => {
    data.value.current_page = 1
    data.value.per_page = value
    getShuttleEpcs()
}
const handleChangePageNumber = (value: number) => {
    data.value.current_page = value
    getShuttleEpcs()
}
const handleChangeSortBy = (sortBy: ESortBy) => {
    sort.value = sortBy
    getShuttleEpcs()
}
</script>

<template>
    <PermissionWrapper :permission="EAdminPerm.SUPER_ADMIN">
        <StackLayout :gap="8">
            <AdminShuttleCreateEpc :loading="data.isLoading" @refresh="getShuttleEpcs" />
            <AdminShuttleCreateBulkEpc :loading="data.isLoading" @refresh="getShuttleEpcs" />
        </StackLayout>
    </PermissionWrapper>
    <FullTableLayout :sortBy="sort" :hideSortBy="true" @changeSortBy="handleChangeSortBy">
        <template #table>
            <BaseTable :data="data.data.shuttleEpcs" :loading="data.isLoading" @click="() => {}">
                <template #columns>
                    <TableColumn field="id" :label="t('shuttleEpc.table.id')" />
                    <TableColumn field="epc" :label="t('shuttleEpc.table.epc')" />
                    <TableColumn :label="t('shuttleEpc.table.createdAt')" v-slot="props">
                        {{ moment(props.row.created_at).format(DATE_FORMAT).toString() }}
                    </TableColumn>
                    <TableActionColumn
                        :hideEdit="true"
                        :deletePermission="EAdminPerm.SUPER_ADMIN"
                        titleItemKey="epc"
                        @delete="deleteShuttleEpc" />
                </template>
            </BaseTable>
        </template>
        <template #pagination>
            <TablePagination
                :perPage="data.per_page"
                :currentPage="data.current_page"
                :lastPage="data.last_page"
                :total="data.total"
                @change:pageSize="handleChangePageSize"
                @change:pageNumber="handleChangePageNumber" />
        </template>
    </FullTableLayout>
</template>
